import React from "react";
import { Container } from "react-bootstrap";
import Header from "../Includes/Header";
import Footer from "../Includes/Footer";
import "../../App.css";

const CookiesPolicy: React.FC = () => {
  return (
    <>
      <Header />
      <Container className="py-5 mt-4" style={{ maxWidth: 820 }}>
        <h1 className="fw-bold mb-1">Cookies Policy</h1>
        <p className="text-secondary small mb-5">Last updated: August 2026</p>

        <Section title="1. What Are Cookies">
          Cookies are small text files placed on your device when you visit a website. They are
          widely used to make websites work efficiently, to remember your preferences, and to
          provide information to website owners. Cookies do not contain any information that
          personally identifies you on their own, but they may be linked to personal data we
          hold about you.
        </Section>

        <Section title="2. How We Use Cookies">
          ARM Financial Services Pty Ltd ("ARMF", "we", "us") uses strictly-necessary cookies and
          browser storage to operate our onboarding platform. Specifically, we use them to:
          <ul className="mt-2">
            <li>Maintain your session securely throughout the onboarding process</li>
            <li>Remember your progress during KYC completion</li>
            <li>Protect against cross-site request forgery (CSRF) attacks</li>
            <li>Ensure the platform functions correctly across page navigations</li>
          </ul>
        </Section>

        <Section title="3. Cookies and Local Storage We Use">
          <ul className="mt-2">
            <li>
              <strong>Strictly Necessary Cookies:</strong> These are essential for the platform to
              function. They enable core features such as session management, authentication state,
              and security. You cannot opt out of these cookies as the platform cannot operate
              without them.
            </li>
            <li className="mt-2">
              <strong>Session Storage:</strong> We use browser session storage (not persistent
              cookies) to temporarily hold your onboarding data (e.g., form progress, uploaded
              documents) during your active session. This data is automatically cleared when you
              close your browser tab.
            </li>
            <li className="mt-2">
              <strong>Local Storage:</strong> We use browser local storage — a different mechanism
              to cookies — to remember your display theme preference (light/dark mode) between
              visits. This stays on your device until you clear your browser's site data.
            </li>
          </ul>
        </Section>

        <Section title="4. Cookies We Do Not Use">
          ARMF does not currently use:
          <ul className="mt-2">
            <li>Analytics or usage-tracking cookies</li>
            <li>Third-party advertising or targeting cookies</li>
            <li>Social media tracking cookies</li>
            <li>Cross-site behavioural tracking cookies</li>
            <li>Any cookies that profile you for marketing purposes</li>
          </ul>
        </Section>

        <Section title="5. Third-Party Cookies">
          Some features of our platform are provided by trusted third-party services, such as
          identity verification providers. These providers may set their own cookies subject to
          their respective privacy policies. We ensure all third-party providers we work with
          comply with applicable data protection regulations.
        </Section>

        <Section title="6. Retention">
          <ul className="mt-2">
            <li><strong>Session cookies:</strong> deleted automatically when you close your browser</li>
            <li><strong>Session storage:</strong> cleared when you close the browser tab</li>
            <li><strong>Local storage (theme preference):</strong> remains until you clear your browser's site data or delete it yourself</li>
          </ul>
        </Section>

        <Section title="7. Managing Your Cookie Preferences">
          Most browsers allow you to view, delete, and block cookies and local storage — refer to
          your browser's help documentation for instructions (e.g., Chrome, Firefox, Safari,
          Edge). Please note that disabling strictly necessary cookies will prevent you from
          completing the onboarding process, as the platform cannot operate without them.
        </Section>

        <Section title="8. Changes to This Policy">
          We may update this Cookies Policy from time to time to reflect changes in technology,
          regulation, or our business practices. We will notify you of material changes by
          updating the "Last updated" date at the top of this page. We encourage you to review
          this policy periodically.
        </Section>

        <Section title="9. Contact Us">
          If you have any questions about our use of cookies or this policy, please contact us
          at <a href="mailto:privacy@armf.com">privacy@armf.com</a>.
        </Section>
      </Container>
      <Footer full={true} />
    </>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h5 className="fw-semibold mb-2">{title}</h5>
    <div className="text-secondary" style={{ lineHeight: 1.8 }}>{children}</div>
  </div>
);

export default CookiesPolicy;
